import { FC } from 'react';
import { SVGIcon } from '../../SVGIcon/SVGIcon';
import { Button } from '../../Button/Button';
import './index.css';
import type {
  RaportSummaryCardProps,
  InfoItemProps,
} from './RaportSummaryCard.types';
import { APP_COLORS } from '@/config/colors';

export const InfoItem: FC<InfoItemProps> = ({
  iconName,
  value,
  label,
  className = '',
}) => (
  <div className={`flex gap-4 items-center ${className}`}>
    <div className="flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#2B4485] p-1">
      <SVGIcon name={iconName} size="md" fill="white" />
    </div>
    <div className="flex flex-col">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-base font-bold leading-5 text-white">{value}</div>
    </div>
  </div>
);

export const RaportSummaryCard: FC<RaportSummaryCardProps> = ({
  innerStats,
  orderNumber,
  raportType,
  packageVariant,
  reportDate,
  vin,
  pageCount,
  logoUrl,
  vehicleBrand,
  vehicleModel,
  downloadPdf = false,
  onDownloadPdf,
  onDownloadPhotos,
  pdfLoading,
  showDownloadPhotos = false,
  i18n,
}) => {
  // i18n fallbacks
  const basicInfoTitle = i18n?.basicInfoTitle || 'Podstawowe informacje';
  const vinNumberLabel = i18n?.vinNumberLabel || 'Numer VIN';
  const orderNumberLabel = i18n?.orderNumberLabel || 'Numer zamówienia';
  const packageVariantLabel = i18n?.packageVariantLabel || 'Wariant pakietu';
  const reportDateLabel = i18n?.reportDateLabel || 'Data raportu';
  const pagesCountTemplate = i18n?.pagesCountTemplate || '{count} stron';
  const reportVolumeLabel = i18n?.reportVolumeLabel || 'Objętość raportu';
  const generatingReportMessage =
    i18n?.generatingReportMessage || 'Generowanie raportu..';
  const downloadPdfButtonText =
    i18n?.downloadPdfButtonText || 'Pobierz raport PDF';
  const downloadPhotosButtonText =
    i18n?.downloadPhotosButtonText || 'Pobierz paczkę zdjęć';
  const downloadingPhotosMessage = 'Pobieranie zdjęć...';

  const pagesCountText = pageCount
    ? pagesCountTemplate.replace('{count}', String(pageCount))
    : '';
  return (
    <div className="summary-card-wrapper">
      <div className="summary-card-title flex items-center justify-between py-3 pl-[20px] md:px-5 md:pl-[40px]">
        <div className="flex flex-col-reverse gap-3 justify-between w-full md:flex-row md:items-center">
          <div className="flex items-center">
            {logoUrl && (
              <img src={logoUrl} alt={vehicleBrand} className="mr-4 w-8 h-8" />
            )}
            <h2 className="text-lg font-semibold">
              {vehicleBrand} <span className="font-normal">{vehicleModel}</span>
            </h2>
          </div>

          {raportType === 'HSP' && (
            <h2 className="hidden text-lg font-semibold xl:block">
              {basicInfoTitle}
            </h2>
          )}
        </div>
      </div>

      <div className="summary-card-content flex flex-col justify-between gap-2 px-5 py-6 pl-[20px] md:flex-row md:pl-[40px] xl:gap-6">
        {raportType === 'HSP' ? (
          <>
            {innerStats ? (
              innerStats
            ) : (
              <div className="flex flex-row gap-2 lg:flex-col lg:gap-6">
                <div>
                  <p className="font-inter text-base text-[#AFC4F0]">
                    {vinNumberLabel}
                  </p>
                  <span className="text-xl font-bold text-white font-archivo">
                    {vin}
                  </span>
                </div>
                <div className="grid flex-1 grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-6">
                  {orderNumber && (
                    <InfoItem
                      iconName="paper"
                      value={orderNumber}
                      label={orderNumberLabel}
                    />
                  )}
                  {packageVariant && (
                    <InfoItem
                      iconName="file-plus"
                      value={packageVariant}
                      label={packageVariantLabel}
                      className="w-40"
                    />
                  )}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="flex flex-col gap-2 lg:flex-col lg:gap-6">
              <div>
                <p className="font-inter text-base text-[#AFC4F0]">
                  {vinNumberLabel}
                </p>
                <span className="text-xl font-bold text-white font-archivo">
                  {vin}
                </span>
              </div>
              <div className="grid flex-1 grid-cols-1 gap-2 lg:grid-cols-2 lg:gap-6">
                {orderNumber && (
                  <InfoItem
                    iconName="paper"
                    value={orderNumber}
                    label={orderNumberLabel}
                  />
                )}
                {packageVariant && (
                  <InfoItem
                    iconName="file-plus"
                    value={packageVariant}
                    label={packageVariantLabel}
                    className="w-40"
                  />
                )}
                <InfoItem
                  iconName="calendar-date"
                  value={reportDate}
                  label={reportDateLabel}
                />
              </div>
            </div>
          </>
        )}

        <div className="h-[1px] bg-[#142555]" />

        <div className="flex flex-col gap-6">
          {raportType === 'HSP' && (
            <InfoItem
              iconName="calendar-date"
              value={reportDate}
              label={reportDateLabel}
            />
          )}

          {pageCount && (
            <div className="flex gap-4 items-center">
              <div className="flex h-11 w-11 items-center justify-center rounded-[10px] bg-[#CA9A39] p-1">
                <SVGIcon name="document-pdf" size="md" fill="text-primary" />
              </div>
              <div className="flex flex-col">
                <span className="text-xl font-bold text-white font-archivo">
                  {pagesCountText}
                </span>
                <span className="font-inter text-base text-[#AFC4F0]">
                  {reportVolumeLabel}
                </span>
              </div>
            </div>
          )}

          {downloadPdf && (
            <div className="flex flex-col gap-3 min-w-[250px] print:hidden">
              <Button
                variant="gold-outline"
                icon={
                  <SVGIcon
                    name="document-pdf"
                    size="md"
                    fill="#FCE2AC"
                    className="ml-4"
                  />
                }
                iconPosition="right"
                onClick={onDownloadPdf}
                isLoading={pdfLoading}
                loadingLabel={generatingReportMessage}
                className="text-sm"
              >
                {downloadPdfButtonText}
              </Button>
              {showDownloadPhotos && onDownloadPhotos && (
                <Button
                  variant="gold-outline"
                  icon={
                    <SVGIcon
                      name="download-arrow"
                      size="md"
                      fill={APP_COLORS.gold.DEFAULT}
                      className="ml-4"
                    />
                  }
                  iconPosition="right"
                  iconColor={APP_COLORS.gold.DEFAULT}
                  onClick={onDownloadPhotos}
                  loadingLabel={downloadingPhotosMessage}
                  className="text-sm"
                >
                  {downloadPhotosButtonText}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
